export const COMPANY_STATUSES = ["active", "paused", "archived"] as const;
export type CompanyStatus = (typeof COMPANY_STATUSES)[number];

export const DEPLOYMENT_MODES = ["local_trusted", "authenticated"] as const;
export type DeploymentMode = (typeof DEPLOYMENT_MODES)[number];

export const DEPLOYMENT_EXPOSURES = ["private", "public"] as const;
export type DeploymentExposure = (typeof DEPLOYMENT_EXPOSURES)[number];

export const AUTH_BASE_URL_MODES = ["auto", "explicit"] as const;
export type AuthBaseUrlMode = (typeof AUTH_BASE_URL_MODES)[number];

export const AGENT_STATUSES = [
  "active",
  "paused",
  "idle",
  "running",
  "error",
  "pending_approval",
  "disabled",
] as const;
export type AgentStatus = (typeof AGENT_STATUSES)[number];

export const AGENT_ADAPTER_TYPES = [
  "process",
  "http",
  "claude_local",
  "codex_local",
  "opencode_local",
  "pi_local",
  "cursor",
  "gateway",
  "bedrock",
] as const;
export type AgentAdapterType = (typeof AGENT_ADAPTER_TYPES)[number];

export const AGENT_TYPES = [
  "supervisor",
  "worker",
  "router",
  "tool_executor",
  "general",
  "metric_monitor",
  "computer_use",
] as const;
export type AgentType = (typeof AGENT_TYPES)[number];

export const AGENT_TYPE_LABELS: Record<AgentType, string> = {
  supervisor: "Supervisor",
  worker: "Worker",
  router: "Router",
  tool_executor: "Tool Executor",
  general: "General",
  metric_monitor: "Metric Monitor",
  computer_use: "Computer Use",
};

/** Default Bedrock models available for agent configuration */
// Only models verified enabled in this Bedrock account (2026-05-31). Opus 4/4.6 and
// Sonnet 4.6 are invalid/legacy-denied here, and the 3.x models are EOL — leaving
// them selectable just 500s on use. Re-add if/when enabled in Bedrock.
export const BEDROCK_MODELS = [
  { id: "us.anthropic.claude-sonnet-4-5-20250929-v1:0", label: "Claude Sonnet 4.5", tier: "standard" },
  { id: "us.anthropic.claude-haiku-4-5-20251001-v1:0", label: "Claude Haiku 4.5", tier: "fast" },
  { id: "us.anthropic.claude-sonnet-4-20250514-v1:0", label: "Claude Sonnet 4", tier: "standard" },
  { id: "us.amazon.nova-pro-v1:0", label: "Amazon Nova Pro", tier: "standard" },
  { id: "us.amazon.nova-lite-v1:0", label: "Amazon Nova Lite", tier: "fast" },
] as const;

/**
 * Bedrock pricing per 1M tokens (USD).
 * Source: https://aws.amazon.com/bedrock/pricing/
 * Updated: 2025-03
 */
export const BEDROCK_PRICING: Record<string, { inputPer1M: number; outputPer1M: number }> = {
  "us.anthropic.claude-opus-4-6-v1:0": { inputPer1M: 15.0, outputPer1M: 75.0 },
  "us.anthropic.claude-sonnet-4-6-v1:0": { inputPer1M: 3.0, outputPer1M: 15.0 },
  "us.anthropic.claude-sonnet-4-5-20250929-v1:0": { inputPer1M: 3.0, outputPer1M: 15.0 },
  "us.anthropic.claude-haiku-4-5-20251001-v1:0": { inputPer1M: 1.0, outputPer1M: 5.0 },
  "us.anthropic.claude-opus-4-20250514-v1:0": { inputPer1M: 15.0, outputPer1M: 75.0 },
  "us.anthropic.claude-sonnet-4-20250514-v1:0": { inputPer1M: 3.0, outputPer1M: 15.0 },
  "us.anthropic.claude-3-5-sonnet-20241022-v2:0": { inputPer1M: 3.0, outputPer1M: 15.0 },
  "us.anthropic.claude-3-5-haiku-20241022-v1:0": { inputPer1M: 0.8, outputPer1M: 4.0 },
  "us.amazon.nova-pro-v1:0": { inputPer1M: 0.8, outputPer1M: 3.2 },
  "us.amazon.nova-lite-v1:0": { inputPer1M: 0.06, outputPer1M: 0.24 },
};

/** Calculate cost in USD from token counts and model ID. */
export function calculateBedrockCostUsd(
  modelId: string,
  inputTokens: number,
  outputTokens: number,
): number {
  const pricing = BEDROCK_PRICING[modelId];
  if (!pricing) return 0;
  return (inputTokens / 1_000_000) * pricing.inputPer1M
       + (outputTokens / 1_000_000) * pricing.outputPer1M;
}

export const AGENT_ICON_NAMES = [
  "bot",
  "cpu",
  "brain",
  "zap",
  "rocket",
  "code",
  "terminal",
  "shield",
  "eye",
  "search",
  "wrench",
  "hammer",
  "lightbulb",
  "sparkles",
  "star",
  "heart",
  "flame",
  "bug",
  "cog",
  "database",
  "globe",
  "lock",
  "mail",
  "message-square",
  "file-code",
  "git-branch",
  "package",
  "puzzle",
  "target",
  "wand",
  "atom",
  "circuit-board",
  "radar",
  "swords",
  "telescope",
  "microscope",
  "crown",
  "gem",
  "hexagon",
  "pentagon",
  "fingerprint",
] as const;
export type AgentIconName = (typeof AGENT_ICON_NAMES)[number];

export const ISSUE_STATUSES = [
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "done",
  "blocked",
  "cancelled",
] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];

export const ISSUE_PRIORITIES = ["critical", "high", "medium", "low"] as const;
export type IssuePriority = (typeof ISSUE_PRIORITIES)[number];

export const GOAL_LEVELS = ["company", "team", "agent", "task"] as const;
export type GoalLevel = (typeof GOAL_LEVELS)[number];

export const GOAL_STATUSES = ["planned", "active", "achieved", "cancelled"] as const;
export type GoalStatus = (typeof GOAL_STATUSES)[number];

export const PROJECT_STATUSES = [
  "backlog",
  "planned",
  "in_progress",
  "completed",
  "cancelled",
] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const PROJECT_COLORS = [
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#ef4444", // red
  "#f97316", // orange
  "#eab308", // yellow
  "#22c55e", // green
  "#14b8a6", // teal
  "#06b6d4", // cyan
  "#3b82f6", // blue
] as const;

export const APPROVAL_TYPES = [
  "approve_strategy",
  "approve_ehr_write",
  "approve_claim_submit",
  "approve_prior_auth",
] as const;
export type ApprovalType = (typeof APPROVAL_TYPES)[number];

export const APPROVAL_STATUSES = [
  "pending",
  "revision_requested",
  "approved",
  "rejected",
  "cancelled",
] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export const SECRET_PROVIDERS = [
  "local_encrypted",
  "aws_kms",
  "aws_secrets_manager",
  "gcp_secret_manager",
  "vault",
] as const;
export type SecretProvider = (typeof SECRET_PROVIDERS)[number];

export const STORAGE_PROVIDERS = ["local_disk", "s3"] as const;
export type StorageProvider = (typeof STORAGE_PROVIDERS)[number];

export const HEARTBEAT_INVOCATION_SOURCES = [
  "timer",
  "assignment",
  "on_demand",
  "automation",
] as const;
export type HeartbeatInvocationSource = (typeof HEARTBEAT_INVOCATION_SOURCES)[number];

export const WAKEUP_TRIGGER_DETAILS = ["manual", "ping", "callback", "system"] as const;
export type WakeupTriggerDetail = (typeof WAKEUP_TRIGGER_DETAILS)[number];

export const WAKEUP_REQUEST_STATUSES = [
  "queued",
  "deferred_issue_execution",
  "claimed",
  "coalesced",
  "skipped",
  "completed",
  "failed",
  "cancelled",
] as const;
export type WakeupRequestStatus = (typeof WAKEUP_REQUEST_STATUSES)[number];

export const HEARTBEAT_RUN_STATUSES = [
  "queued",
  "running",
  "succeeded",
  "failed",
  "cancelled",
  "timed_out",
] as const;
export type HeartbeatRunStatus = (typeof HEARTBEAT_RUN_STATUSES)[number];

export const LIVE_EVENT_TYPES = [
  "heartbeat.run.queued",
  "heartbeat.run.status",
  "heartbeat.run.event",
  "heartbeat.run.log",
  "agent.status",
  "activity.logged",
  "agent.error_alert",
  "webhook.received",
  "metric.alert",
] as const;
export type LiveEventType = (typeof LIVE_EVENT_TYPES)[number];

export const WEBHOOK_EVENT_TYPES = [
  "ehr_update",
  "claim_status_change",
  "prior_auth_decision",
  "lab_result",
  "eligibility_response",
  "appointment_reminder",
  "patient_message",
  "referral_update",
] as const;
export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[number];

export const PRINCIPAL_TYPES = ["user", "agent"] as const;
export type PrincipalType = (typeof PRINCIPAL_TYPES)[number];

export const MEMBERSHIP_STATUSES = ["pending", "active", "suspended"] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

export const INSTANCE_USER_ROLES = ["instance_admin"] as const;
export type InstanceUserRole = (typeof INSTANCE_USER_ROLES)[number];

export const INVITE_TYPES = ["company_join"] as const;
export type InviteType = (typeof INVITE_TYPES)[number];

export const INVITE_JOIN_TYPES = ["human", "agent", "both"] as const;
export type InviteJoinType = (typeof INVITE_JOIN_TYPES)[number];

export const JOIN_REQUEST_TYPES = ["human", "agent"] as const;
export type JoinRequestType = (typeof JOIN_REQUEST_TYPES)[number];

export const JOIN_REQUEST_STATUSES = ["pending_approval", "approved", "rejected"] as const;
export type JoinRequestStatus = (typeof JOIN_REQUEST_STATUSES)[number];

export const PERMISSION_KEYS = [
  "agents:create",
  "users:invite",
  "users:manage_permissions",
  "tasks:assign",
  "tasks:assign_scope",
  "joins:approve",
  // Tool-level permissions for agent RBAC
  "tools:ehr:read",
  "tools:ehr:write",
  "tools:payer:check_eligibility",
  "tools:payer:submit_claim",
  "tools:payer:prior_auth",
  "tools:comms:send",
  "tools:patient:read",
  "tools:patient:write",
  "tools:document:read",
  "tools:document:write",
  "tools:analytics:read",
  "tools:analytics:write",
  // Workflow management permissions
  "workflows:manage",
  "workflows:fire_event",
] as const;
export type PermissionKey = (typeof PERMISSION_KEYS)[number];
